import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Search, Plus, Building2, Download, Sparkles, Loader2, Upload } from "lucide-react";

const CURRENT_YEAR = new Date().getFullYear();
const MONTHS = [
  "Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie",
  "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie",
];

const EMPTY_FORM = {
  cui: "",
  denumire: "",
  adresa: "",
  judet: "",
  localitate: "",
  nr_reg_com: "",
  telefon: "",
  email: "",
  persoana_contact: "",
  observatii: "",
};

export default function ClientsList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [frecventa, setFrecventa] = useState(0); // 0 = all, 1 | 3 | 12
  const [expiring, setExpiring] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [importingXls, setImportingXls] = useState(false);

  const importXls = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be selected again
    if (!file) return;
    if (!/\.(xls|xlsx)$/i.test(file.name)) {
      toast.error("Alege un fișier .xls sau .xlsx");
      return;
    }
    setImportingXls(true);
    const t = toast.loading("Se importă clienții din Excel...");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import-xls", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.dismiss(t);
      toast.success(
        `Import gata: ${data.imported} clienți noi (${data.already_exists} existenți, ${data.dup_in_file} dubluri, ${data.no_cui} fără CUI)`,
        { duration: 8000 }
      );
      load();
    } catch (err) {
      toast.dismiss(t);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la import");
    } finally {
      setImportingXls(false);
    }
  };

  const load = async () => {
    setLoading(true);
    const params = {
      year: year === "all" ? undefined : year,
      month: month === "all" ? undefined : month,
      q: q || undefined,
      frecventa: frecventa || undefined,
      expiring: expiring ? "true" : undefined,
    };
    const [list, stats] = await Promise.all([
      api.get("/clients", { params }),
      api.get("/clients/stats"),
    ]);
    setRows(list.data);
    setYears(stats.data.years || []);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, frecventa, expiring]);

  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const downloadExcel = async () => {
    setDownloadingXlsx(true);
    try {
      const { data } = await api.get("/export/xlsx", {
        params: {
          year: year === "all" ? undefined : year,
          month: month === "all" ? undefined : month,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `imaggio_export${year !== "all" ? `_${year}` : ""}${month !== "all" ? `_${String(month).padStart(2, "0")}` : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descărcat");
    } catch (e) {
      toast.error("Eroare la export");
    } finally {
      setDownloadingXlsx(false);
    }
  };

  const yearOptions = useMemo(() => {
    const s = new Set([CURRENT_YEAR, ...years]);
    return Array.from(s).sort((a, b) => b - a);
  }, [years]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const fetchFromANAF = async () => {
    const cui = form.cui.trim();
    if (!cui) {
      toast.error("Introdu mai întâi CUI-ul");
      return;
    }
    setFetching(true);
    try {
      const { data } = await api.get(`/anaf/${cui}`);
      setForm((f) => ({
        ...f,
        cui: data.cui || cui,
        denumire: data.denumire || f.denumire,
        adresa: data.adresa || f.adresa,
        judet: data.judet || f.judet,
        localitate: data.localitate || f.localitate,
        nr_reg_com: data.nr_reg_com || f.nr_reg_com,
        telefon: data.telefon || f.telefon,
      }));
      toast.success("Date preluate de la ANAF");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare ANAF");
    } finally {
      setFetching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.cui.trim() || !form.denumire.trim()) {
      toast.error("CUI și denumirea sunt obligatorii");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clients", form);
      toast.success("Client adăugat");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare salvare");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-eyebrow">Bază de date</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Clienți</h1>
          <p className="text-muted-foreground mt-1">
            {loading ? "Se încarcă..." : `${rows.length} clienți afișați`}
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="btn-gold hover:btn-gold h-11 px-5"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Adaugă client
        </Button>
      </header>

      <div className="bg-white border border-border rounded-md p-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Caută după denumire sau CUI..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 bg-white"
            data-testid="clients-search"
          />
        </div>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-full md:w-32 h-10 bg-white" data-testid="clients-year-filter">
            <SelectValue placeholder="An" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toți anii</SelectItem>
            {yearOptions.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={month} onValueChange={setMonth} disabled={year === "all"}>
          <SelectTrigger className="w-full md:w-40 h-10 bg-white" data-testid="clients-month-filter">
            <SelectValue placeholder="Toate lunile" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toate lunile</SelectItem>
            {MONTHS.map((m, i) => (
              <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={downloadExcel}
          disabled={downloadingXlsx}
          variant="outline"
          className="h-10 border-gold text-gold-deep hover:bg-gold hover:text-white"
          data-testid="download-xlsx-btn"
        >
          {downloadingXlsx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Excel
        </Button>
        <label className={`inline-flex items-center h-10 px-4 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-md cursor-pointer text-sm font-medium transition-colors ${importingXls ? "opacity-60 pointer-events-none" : ""}`}>
          {importingXls ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          Import Excel
          <input
            type="file"
            accept=".xls,.xlsx"
            onChange={importXls}
            className="hidden"
            data-testid="import-xls-input"
          />
        </label>
      </div>

      {/* Filtre pe frecvență contract + expirare */}
      <div className="flex flex-wrap items-center gap-2 -mt-2">
        <span className="label-eyebrow mr-1">Filtre:</span>
        {[
          { v: 1, label: "La 1 lună" },
          { v: 3, label: "La 3 luni" },
          { v: 12, label: "La 12 luni" },
        ].map((f) => (
          <button
            key={f.v}
            type="button"
            onClick={() => setFrecventa(frecventa === f.v ? 0 : f.v)}
            data-testid={`filter-freq-${f.v}`}
            className={`text-xs font-medium border rounded-full px-3 py-1.5 transition-colors ${
              frecventa === f.v ? "border-gold bg-gold text-white" : "border-border bg-white hover:border-gold"
            }`}
          >
            {frecventa === f.v ? "☑ " : "☐ "}{f.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setExpiring(!expiring)}
          data-testid="filter-expiring"
          className={`text-xs font-medium border rounded-full px-3 py-1.5 transition-colors ${
            expiring ? "border-red-500 bg-red-50 text-red-700" : "border-border bg-white hover:border-red-500"
          }`}
        >
          {expiring ? "☑ " : "☐ "}Expiră luna asta
        </button>
        {(frecventa || expiring) && (
          <button
            type="button"
            onClick={() => { setFrecventa(0); setExpiring(false); }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Șterge filtrele
          </button>
        )}
      </div>

      <div className="bg-white border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="label-eyebrow text-foreground">Client</TableHead>
              <TableHead className="label-eyebrow text-foreground">CUI</TableHead>
              <TableHead className="label-eyebrow text-foreground hidden md:table-cell">Localitate</TableHead>
              <TableHead className="label-eyebrow text-foreground text-center">Contracte</TableHead>
              <TableHead className="label-eyebrow text-foreground text-center">PV-uri</TableHead>
              <TableHead className="label-eyebrow text-foreground hidden lg:table-cell">Adăugat</TableHead>
              <TableHead className="label-eyebrow text-foreground hidden lg:table-cell">Expiră contract</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Se încarcă...</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-16">
                  <Building2 className="w-8 h-8 mx-auto text-muted-foreground" />
                  <p className="mt-3 font-medium">Niciun client găsit</p>
                  <p className="text-sm text-muted-foreground">Modifică filtrele sau adaugă un client nou.</p>
                </TableCell>
              </TableRow>
            ) : rows.map((c) => (
              <TableRow key={c.id} className="group" data-testid={`client-row-${c.id}`}>
                <TableCell>
                  <Link to={`/clienti/${c.id}`} className="font-medium hover:text-gold-deep transition-colors">
                    {c.denumire}
                  </Link>
                  <p className="text-xs text-muted-foreground truncate max-w-xs">{c.adresa}</p>
                </TableCell>
                <TableCell className="font-mono-crm text-sm">{c.cui}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{c.localitate || c.judet || "—"}</TableCell>
                <TableCell className="text-center font-medium">{c.contracts_count}</TableCell>
                <TableCell className="text-center font-medium">{c.pv_count}</TableCell>
                <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString("ro-RO") : "—"}
                </TableCell>
                <TableCell className="hidden lg:table-cell text-xs">
                  {c.latest_contract_expira ? (
                    (() => {
                      const zile = c.latest_contract_expira_zile;
                      const dateStr = new Date(c.latest_contract_expira).toLocaleDateString("ro-RO");
                      let cls = "text-muted-foreground";
                      let suf = "";
                      if (zile !== null && zile !== undefined) {
                        if (zile < 0) { cls = "text-red-700 font-semibold"; suf = ` · expirat de ${-zile} zile`; }
                        else if (zile <= 30) { cls = "text-red-600 font-semibold"; suf = ` · în ${zile} zile`; }
                        else if (zile <= 60) { cls = "text-amber-700 font-semibold"; suf = ` · în ${zile} zile`; }
                      }
                      return <span className={cls}>{dateStr}<span className="text-[10px] font-normal">{suf}</span></span>;
                    })()
                  ) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Link
                    to={`/clienti/${c.id}`}
                    className="text-sm text-slate-700 hover:text-gold-deep font-medium transition-colors"
                    data-testid={`open-client-${c.id}`}
                  >
                    Deschide →
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[92vw] max-w-md sm:max-w-lg max-h-[85dvh] xl:max-h-[90dvh] overflow-y-auto p-4 xl:p-5">
          <DialogHeader className="space-y-1">
            <DialogTitle className="font-display text-lg xl:text-xl">Client nou</DialogTitle>
            <DialogDescription className="text-xs">
              Introdu CUI-ul, apoi apasă <b>Preia din ANAF</b>. Restul câmpurilor se completează automat.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submit} className="space-y-2.5">
            <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <Label htmlFor="cui" className="label-eyebrow text-[10px]">CUI *</Label>
                <Input
                  id="cui"
                  value={form.cui}
                  onChange={(e) => setForm({ ...form, cui: e.target.value })}
                  placeholder="ex: RO14399840 sau 14399840"
                  className="mt-1 h-9"
                  required
                  data-testid="client-cui-input"
                />
              </div>
              <Button
                type="button"
                onClick={fetchFromANAF}
                disabled={fetching || !form.cui}
                variant="outline"
                size="sm"
                className="h-9 border-gold text-gold-deep hover:bg-gold hover:text-white transition-colors"
                data-testid="fetch-anaf-btn"
              >
                {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                Preia din ANAF
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-2.5">
              <div className="sm:col-span-2">
                <Label className="label-eyebrow text-[10px]">Denumire firmă *</Label>
                <Input
                  value={form.denumire}
                  onChange={(e) => setForm({ ...form, denumire: e.target.value })}
                  className="mt-1 h-9"
                  required
                  data-testid="client-denumire-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="label-eyebrow text-[10px]">Adresă</Label>
                <Input value={form.adresa} onChange={(e) => setForm({ ...form, adresa: e.target.value })} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow text-[10px]">Județ</Label>
                <Input value={form.judet} onChange={(e) => setForm({ ...form, judet: e.target.value })} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow text-[10px]">Localitate</Label>
                <Input value={form.localitate} onChange={(e) => setForm({ ...form, localitate: e.target.value })} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow text-[10px]">Nr. Reg. Com.</Label>
                <Input value={form.nr_reg_com} onChange={(e) => setForm({ ...form, nr_reg_com: e.target.value })} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow text-[10px]">Telefon</Label>
                <Input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow text-[10px]">Email</Label>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="label-eyebrow text-[10px]">Persoană contact</Label>
                <Input value={form.persoana_contact} onChange={(e) => setForm({ ...form, persoana_contact: e.target.value })} className="mt-1 h-9" />
              </div>
              <div className="sm:col-span-2">
                <Label className="label-eyebrow text-[10px]">Observații</Label>
                <Textarea value={form.observatii} onChange={(e) => setForm({ ...form, observatii: e.target.value })} className="mt-1 min-h-0" rows={2} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
              <Button
                type="submit"
                disabled={saving}
                className="btn-gold hover:btn-gold"
                data-testid="save-client-btn"
              >
                {saving ? "Se salvează..." : "Salvează client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
